import React, { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { ButtonMenuItem, COLLAPSE_ELEM_BEFORE } from '@atlaskit/side-nav-items/button-menu-item';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { Divider, MenuSection } from '@atlaskit/side-nav-items/menu-section';

import { useFirePostOfficeTrigger } from '@atlassian/post-office-context';

import type { RecommendedConnector } from '../connector-promo-config-types.ts';
import { useAdminHubConnectorLink } from '../hooks/useAdminHubConnectorLink.ts';

import { ConnectorDismissMenu } from './ConnectorDismissMenu.tsx';
import { ConnectorIconPlaceholder } from './ConnectorIconPlaceholder.tsx';

const ConnectorRow = ({
	connector,
	onSetup,
	setupLabel,
	testId,
}: {
	connector: RecommendedConnector;
	onSetup: (key: string) => void;
	setupLabel: string;
	testId: string;
}) => (
	<ButtonMenuItem
		elemBefore={
			<ConnectorIconPlaceholder name={connector.displayName} iconUrl={undefined} size={20} />
		}
		elemAfter={<Text color="color.text.brand">{setupLabel}</Text>}
		onClick={() => onSetup(connector.key)}
		testId={testId}
	>
		{connector.displayName}
	</ButtonMenuItem>
);

const messages = defineMessages({
	label: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.searchAcrossTools.label',
		defaultMessage: 'Search across tools',
		description: 'Label for the sidebar section showing connector tools',
	},
	setupLabel: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.searchAcrossTools.setupLabel',
		defaultMessage: 'Setup',
		description: 'Button label to set up a connector',
	},
});

export interface SearchAcrossToolsItemProps {
	recommendedConnectors: RecommendedConnector[];
	onSetupConnector: (connectorKey: string) => void;
	onDismiss: () => void;
	/** Whether the spotlight should be shown on the header row. */
	shouldShowSpotlight: boolean;
	/** Called when the spotlight trigger fires; should persist spotlightShown: true. */
	onDismissSpotlight: () => void;
	testId?: string;
}

/** The triggerId registered in Post Office for the Search Across Tools spotlight. */
const SPOTLIGHT_TRIGGER_ID = 'spotlight-search-across-tools';

export const SearchAcrossToolsItem = ({
	recommendedConnectors,
	onSetupConnector,
	onDismiss,
	shouldShowSpotlight,
	onDismissSpotlight,
	testId = SPOTLIGHT_TRIGGER_ID,
}: SearchAcrossToolsItemProps) => {
	const { formatMessage } = useIntl();
	const adminHubLink = useAdminHubConnectorLink();

	const handleSetupClick = useCallback(
		(key: string) => {
			onSetupConnector(key);
		},
		[onSetupConnector],
	);

	const { fireTrigger } = useFirePostOfficeTrigger({ triggerId: SPOTLIGHT_TRIGGER_ID });

	useEffect(() => {
		if (!shouldShowSpotlight) {
			return;
		}
		// Persist that the spotlight has been shown before firing the trigger,
		// so it won't re-show even if the component re-mounts before state settles.
		onDismissSpotlight();
		fireTrigger({ isActive: true });

		return () => {
			fireTrigger({ isActive: false });
		};
	}, [shouldShowSpotlight, fireTrigger, onDismissSpotlight]);

	return (
		<MenuSection>
			<Divider />
			<LinkMenuItem
				href={adminHubLink ?? '/admin'}
				target="_blank"
				elemAfter={<LinkExternalIcon label="" color="currentColor" size="small" />}
				elemBefore={COLLAPSE_ELEM_BEFORE}
				actions={
					<Box paddingInlineStart="space.025">
						<ConnectorDismissMenu onDismiss={onDismiss} testId={`${testId}--dismiss-menu`} />
					</Box>
				}
				testId={`${testId}--header`}
			>
				{formatMessage(messages.label)}
			</LinkMenuItem>
			{recommendedConnectors.slice(0, 3).map((connector) => (
				<ConnectorRow
					key={connector.key}
					connector={connector}
					onSetup={handleSetupClick}
					setupLabel={formatMessage(messages.setupLabel)}
					testId={`${testId}--connector-${connector.key}`}
				/>
			))}
		</MenuSection>
	);
};
